import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner, Modal, Button } from "react-bootstrap";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../apiConfig";
import { logout } from "../../auth";

// ── Inject Geist font + keyframes once ──────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("dash-ap-styles")) {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap";
  document.head.appendChild(link);

  const style = document.createElement("style");
  style.id = "dash-ap-styles";
  style.textContent = `
    @keyframes dash-pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
    @keyframes dash-fadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  `;
  document.head.appendChild(style);
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  bg:         "#f2f1ee",
  surface:    "rgba(255,255,255,0.72)",
  surface2:   "#f9f8f6",
  ink:        "#1A1A1C",
  ink2:       "#4A4A50",
  ink3:       "#86868B",
  ink4:       "#B4B3B0",
  hair:       "rgba(20,20,22,0.07)",
  hair2:      "rgba(20,20,22,0.04)",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  ok:         "#1E8E3E",
  okSoft:     "rgba(30,142,62,0.10)",
  warn:       "#B25E09",
  warnSoft:   "rgba(178,94,9,0.10)",
  danger:     "#C5221F",
  dangerSoft: "rgba(197,34,31,0.10)",
  shadowSm:   "0 1px 2px rgba(20,20,22,0.04),0 1px 1px rgba(20,20,22,0.03)",
  shadowMd:   "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
  font:       "'Geist',-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
  mono:       "'Geist Mono','SF Mono',ui-monospace,Menlo,monospace",
  r:          18,
};



// ── Helpers ───────────────────────────────────────────────────────────────────
function toTitleCase(s) {
  if (!s) return s;
  return String(s).toLowerCase().split(/\s+/).map(w => w ? w[0].toUpperCase() + w.slice(1) : "").join(" ");
}

function initials(name = "") {
  return (name || "").split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?";
}

const PROV_MAP = {
  "Claro":        { c: "#E50914", l: "CL" },
  "Ligga Telecom":{ c: "#7B2CBF", l: "LG" },
  "Vivo":         { c: "#660099", l: "VV" },
  "Ired Telecom": { c: "#1a3a6b", l: "IR" },
};
function provMark(name) {
  return PROV_MAP[name] || { c: "#7B7B7B", l: (name || "??").slice(0, 2).toUpperCase() };
}

const AVATAR_COLORS = [
  { bg: "rgba(30,58,138,0.10)",  color: "#1e3a8a" },
  { bg: "rgba(30,142,62,0.10)",  color: "#1E8E3E" },
  { bg: "rgba(178,94,9,0.10)",   color: "#B25E09" },
  { bg: "rgba(197,34,31,0.10)",  color: "#C5221F" },
  { bg: "rgba(107,64,186,0.10)", color: "#6B40BA" },
];

const STATUS_AVATAR = {
  ATIVADO:   { bg: "rgba(30,142,62,0.10)",  color: "#1E8E3E" },
  PENDENTE:  { bg: "rgba(178,94,9,0.10)",   color: "#B25E09" },
  NEGATIVA:  { bg: "rgba(197,34,31,0.10)",  color: "#C5221F" },
  INATIVADO: { bg: "rgba(20,20,22,0.06)",   color: "#86868b" },
  PAGO:      { bg: "rgba(15,118,110,0.10)", color: "#0F766E" },
};
function statusAvatar(item) {
  if (item.disabledAt) return STATUS_AVATAR.INATIVADO;
  const s = String(item.status || "PENDENTE").toUpperCase();
  if (s === "ATIVADO" && item.paidAt) return STATUS_AVATAR.PAGO;
  return STATUS_AVATAR[s] || STATUS_AVATAR.PENDENTE;
}

function activationStatus(act) {
  if (act.disabledAt) return { label: "Inativado", color: T.ink3, bg: T.surface2 };
  const s = String(act.status || "PENDENTE").toUpperCase();
  // "Pago" = ATIVADO com paidAt; continua contando como ativa nos totais
  if (s === "ATIVADO" && act.paidAt) return { label: "Pago", color: STATUS_AVATAR.PAGO.color, bg: STATUS_AVATAR.PAGO.bg };
  if (s === "ATIVADO") return { label: "Ativado",  color: T.ok,   bg: T.okSoft   };
  return                        { label: "Pendente", color: T.warn, bg: T.warnSoft };
}

// ── Sparkline ─────────────────────────────────────────────────────────────────
function Sparkline({ data, color = T.accent, width = 120, height = 36 }) {
  const id = useMemo(() => "sp-" + Math.random().toString(36).slice(2, 8), []);
  const lineRef = useRef(null);
  const [areaVis, setAreaVis] = useState(false);
  const hasData = data && data.length >= 2;

  const { line, areaPath } = useMemo(() => {
    if (!hasData) return { line: "", areaPath: "" };
    const max = Math.max(...data, 1), min = Math.min(...data, 0), range = max - min || 1;
    const step = width / (data.length - 1);
    const pts = data.map((v, i) => [i * step, height - ((v - min) / range) * (height - 4) - 2]);
    const ln = pts.reduce((acc, [x, y], i) => {
      if (i === 0) return `M ${x} ${y}`;
      const [px, py] = pts[i - 1];
      return `${acc} C ${px + step / 2} ${py},${x - step / 2} ${y},${x} ${y}`;
    }, "");
    return { line: ln, areaPath: `${ln} L ${width} ${height} L 0 ${height} Z` };
  }, [data, hasData, width, height]);

  useEffect(() => {
    if (!hasData || !lineRef.current) return;
    setAreaVis(false);
    const el = lineRef.current;
    const len = el.getTotalLength();
    el.style.transition = "none";
    el.style.strokeDasharray = `${len}`;
    el.style.strokeDashoffset = `${len}`;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => {
      el.style.transition = "stroke-dashoffset 0.85s cubic-bezier(0.4,0,0.2,1)";
      el.style.strokeDashoffset = "0";
      setTimeout(() => setAreaVis(true), 350);
    }));
    return () => cancelAnimationFrame(raf);
  }, [data, hasData]);

  if (!hasData) return null;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }}>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${id})`} style={{ opacity: areaVis ? 1 : 0, transition: "opacity 0.5s ease" }} />
      <path ref={lineRef} d={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KPI({ label, value, sub, delta, deltaDir, iconEl, iconTone, spark, sparkColor, onClick }) {
  const toneColors = {
    accent: { bg: T.accentSoft, color: T.accent },
    ok:     { bg: T.okSoft,     color: T.ok     },
    warn:   { bg: T.warnSoft,   color: T.warn   },
    def:    { bg: T.surface2,   color: T.ink2   },
  };
  const tone = toneColors[iconTone] || toneColors.def;
  const deltaStyle = {
    up:   { color: T.ok,   bg: T.okSoft   },
    down: { color: T.danger, bg: T.dangerSoft },
    flat: { color: T.ink3, bg: "#EFECE5"  },
  }[deltaDir] || { color: T.ink3, bg: "#EFECE5" };
  return (
    <div
      onClick={onClick}
      style={{
        background: T.surface, border: `1px solid ${T.hair}`, borderRadius: T.r,
        boxShadow: T.shadowSm, padding: "18px 18px 14px",
        display: "flex", flexDirection: "column", gap: 10, minHeight: 132,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ fontSize: 12, color: T.ink3, fontWeight: 500, letterSpacing: "0.005em" }}>{label}</div>
        {iconEl && (
          <div style={{ width: 28, height: 28, borderRadius: 8, background: tone.bg, color: tone.color, display: "grid", placeItems: "center" }}>
            {iconEl}
          </div>
        )}
      </div>
      <div style={{ fontSize: 36, fontWeight: 600, letterSpacing: "-0.04em", lineHeight: 1, fontVariantNumeric: "tabular-nums", color: T.ink }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: T.ink3, display: "flex", alignItems: "center", gap: 6 }}>
        {delta != null && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11.5, fontWeight: 500, padding: "2px 7px", borderRadius: 999, color: deltaStyle.color, background: deltaStyle.bg }}>
            {deltaDir === "up" && "↑"}{deltaDir === "down" && "↓"}
            {delta}
          </span>
        )}
        <span>{sub}</span>
      </div>
      {spark && (
        <div style={{ marginTop: "auto" }}>
          <Sparkline data={spark} color={sparkColor || T.accent} width={220} height={32} />
        </div>
      )}
    </div>
  );
}

// ── Line chart (SVG) ─────────────────────────────────────────────────────────
const PEND_C = "#0891b2";

function LineChart({ data, onClickBar, isDaily }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [areaVis, setAreaVis]   = useState(false);
  const ativosRef = useRef(null);
  const pendRef   = useRef(null);
  const negRef    = useRef(null);
  const animKey   = data.map(d => `${d.key}:${d.ativos}`).join(",");

  useEffect(() => {
    setAreaVis(false);
    const prep = (ref) => {
      if (!ref.current) return;
      const el = ref.current, len = el.getTotalLength();
      el.style.transition = "none";
      el.style.strokeDasharray = `${len}`;
      el.style.strokeDashoffset = `${len}`;
    };
    prep(ativosRef); prep(pendRef); prep(negRef);
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => {
      [[ativosRef, 0], [pendRef, 100], [negRef, 200]].forEach(([ref, delay]) => {
        if (!ref.current) return;
        ref.current.style.transition = `stroke-dashoffset 1.1s cubic-bezier(0.4,0,0.2,1) ${delay}ms`;
        ref.current.style.strokeDashoffset = "0";
      });
      setTimeout(() => setAreaVis(true), 500);
    }));
    return () => cancelAnimationFrame(raf);
  }, [animKey]); // eslint-disable-line

  const W = 760, H = 240, padL = 6, padR = 6, padT = 24, padB = 28;
  const inner = W - padL - padR, chartH = H - padT - padB;
  const n = data.length;
  const maxVal = Math.max(...data.map(d => Math.max(d.ativos + d.pendentes, d.negativas||0)), 1) * 1.18;
  const hasPend = data.some(d => d.pendentes > 0);
  const hasNeg  = data.some(d => (d.negativas||0) > 0);

  const xOf = i => padL + (inner / Math.max(n - 1, 1)) * i;
  const yOf = v => padT + chartH - (v / maxVal) * chartH;

  const ativosPts = data.map((d, i) => [xOf(i), yOf(d.ativos),        d.ativos]);
  const pendPts   = data.map((d, i) => [xOf(i), yOf(d.pendentes),      d.pendentes]);
  const negPts    = data.map((d, i) => [xOf(i), yOf(d.negativas||0),   d.negativas||0]);
  // Projeção: ativos + pendentes (se todas as pendentes ativarem). Onde não há pendentes coincide com a linha de ativos
  const projPts   = data.map((d, i) => [xOf(i), yOf(d.ativos + d.pendentes), d.ativos + d.pendentes]);
  const lastProjIdx = data.reduce((acc, d, i) => d.pendentes > 0 ? i : acc, -1);

  const baseY = padT + chartH;

  function bezier(pts) {
    return pts.reduce((acc, [x, y], i) => {
      if (i === 0) return `M ${x.toFixed(1)} ${y.toFixed(1)}`;
      const [px, py] = pts[i - 1];
      const c = (x - px) * 0.4;
      return `${acc} C ${(px+c).toFixed(1)} ${py.toFixed(1)},${(x-c).toFixed(1)} ${y.toFixed(1)},${x.toFixed(1)} ${y.toFixed(1)}`;
    }, "");
  }

  function segments(allPts) {
    const segs = []; let seg = [];
    allPts.forEach(pt => {
      if (pt[2] > 0) { seg.push(pt); }
      else { if (seg.length > 1) segs.push([...seg]); seg = []; }
    });
    if (seg.length > 1) segs.push(seg);
    return segs;
  }

  function gappedCurve(allPts) {
    return segments(allPts).map(s => bezier(s)).join(" ");
  }
  function gappedArea(allPts) {
    return segments(allPts).map(s =>
      `${bezier(s)} L ${s[s.length-1][0].toFixed(1)} ${baseY} L ${s[0][0].toFixed(1)} ${baseY} Z`
    ).join(" ");
  }

  const ativosCurve = bezier(ativosPts);
  const pendCurve   = bezier(pendPts);
  const negCurve    = bezier(negPts);
  const projCurve   = bezier(projPts);
  const ativosArea  = `${bezier(ativosPts)} L ${ativosPts[ativosPts.length-1][0].toFixed(1)} ${baseY} L ${ativosPts[0][0].toFixed(1)} ${baseY} Z`;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, display: "block" }}>
        <defs>
          <linearGradient id="lc-ativos" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={T.accent} stopOpacity="0.14" />
            <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Grid */}
        {[0,1,2,3,4].map(i => {
          const y = padT + (chartH / 4) * i;
          return <line key={i} x1={padL} x2={W-padR} y1={y} y2={y} stroke={T.hair2} strokeDasharray={i===4?"":"3 5"} />;
        })}

        {/* Ativos area */}
        <path d={ativosArea} fill="url(#lc-ativos)" style={{ opacity: areaVis ? 1 : 0, transition: "opacity 0.7s ease" }} />

        {/* Lines — negativas first (back), then pendentes, then ativos on top */}
        {hasNeg  && <path ref={negRef}    d={negCurve}    fill="none" stroke={T.danger} strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.8" />}
        {hasPend && <path ref={pendRef}   d={pendCurve}   fill="none" stroke={PEND_C}   strokeWidth="1.8" strokeLinecap="round" />}
        <path ref={ativosRef} d={ativosCurve} fill="none" stroke={T.accent} strokeWidth="2.4" strokeLinecap="round" />
        {hasPend && <path d={projCurve} fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeDasharray="5 5" strokeOpacity="0.55" style={{ opacity: areaVis ? 1 : 0, transition: "opacity 0.7s ease" }} />}

        {/* Projeção do último período com pendentes */}
        {lastProjIdx >= 0 && hoverIdx !== lastProjIdx && (() => {
          const [px, py, pv] = projPts[lastProjIdx];
          const labelY = Math.max(py - 9, 11);
          return (
            <g style={{ opacity: areaVis ? 1 : 0, transition: "opacity 0.7s ease", pointerEvents: "none" }}>
              <circle cx={px} cy={py} r={4} fill={T.surface} stroke={T.accent} strokeWidth={2} strokeDasharray="2 2" />
              <text x={px} y={labelY} textAnchor={lastProjIdx === n - 1 ? "end" : "middle"}
                style={{ fontSize: 10.5, fill: T.accent, fontFamily: T.mono, fontWeight: 600 }}>
                {pv}
              </text>
            </g>
          );
        })()}

        {/* Hover hit areas + x-axis labels */}
        {ativosPts.map(([x,, v], i) => {
          const isHov = hoverIdx === i;
          const showLabel = isHov || (!isDaily) || (isDaily && (i % 5 === 0 || i === n - 1));
          const yA = ativosPts[i][1], yP = pendPts[i][1], yN = negPts[i][1];
          return (
            <g key={i}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onClick={() => { if (!isDaily && data[i].count > 0 && onClickBar) onClickBar(data[i]); }}
              style={{ cursor: !isDaily && data[i].count > 0 ? "pointer" : "default" }}
            >
              <rect x={x - 18} y={padT} width={36} height={chartH} fill="transparent" />
              {/* value label for ativos above the line */}
              {v > 0 && (
                <text x={x} y={yA - 9} textAnchor="middle"
                  style={{ fontSize: 10.5, fill: isHov ? T.ink : T.ink3, fontFamily: T.mono, fontWeight: 600, opacity: showLabel ? 1 : 0.5, transition: "opacity 120ms,fill 120ms" }}>
                  {v}
                </text>
              )}
              {/* hover dots on all 3 series */}
              {isHov && data[i].ativos > 0    && <circle cx={x} cy={yA} r={5} fill={T.surface} stroke={T.accent} strokeWidth={2.5} />}
              {isHov && data[i].pendentes > 0 && <circle cx={x} cy={projPts[i][1]} r={4.5} fill={T.surface} stroke={T.accent} strokeWidth={2} strokeDasharray="2 2" />}
              {isHov && data[i].pendentes > 0 && <circle cx={x} cy={yP} r={4.5} fill={T.surface} stroke={PEND_C}   strokeWidth={2} />}
              {isHov && (data[i].negativas||0) > 0 && <circle cx={x} cy={yN} r={4.5} fill={T.surface} stroke={T.danger} strokeWidth={2} />}
              <text x={x} y={H - 7} textAnchor="middle"
                style={{ fontSize: isDaily ? 9 : 10.5, fill: T.ink3, fontWeight: 500, opacity: showLabel ? 1 : 0 }}>
                {data[i].label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {hoverIdx !== null && (() => {
        const d = data[hoverIdx];
        const xPct = (hoverIdx / Math.max(n - 1, 1)) * 100;
        const onLeft = hoverIdx > n / 2;
        return (
          <div style={{
            position: "absolute", top: 6,
            left:  onLeft ? "auto" : `calc(${xPct}% + 18px)`,
            right: onLeft ? `calc(${100 - xPct}% + 18px)` : "auto",
            background: T.surface, color: T.ink,
            padding: "12px 14px", borderRadius: 12, fontSize: 12,
            whiteSpace: "nowrap", minWidth: 200,
            boxShadow: "0 16px 40px -12px rgba(20,20,22,0.18),0 4px 12px rgba(20,20,22,0.08)",
            border: `1px solid ${T.hair}`, pointerEvents: "none",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 12.5 }}>{d.label}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${T.hair2}` }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
                <span style={{ width: 8, height: 2, borderRadius: 1, background: T.accent, display: "inline-block" }} />
                Ativos: <b style={{ marginLeft: 2, fontFamily: T.mono }}>{d.ativos}</b>
              </span>
              {d.pendentes > 0 && (
                <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
                  <span style={{ width: 8, height: 2, borderRadius: 1, background: PEND_C, display: "inline-block" }} />
                  Pendentes: <b style={{ marginLeft: 2, fontFamily: T.mono }}>{d.pendentes}</b>
                </span>
              )}
              {(d.negativas||0) > 0 && (
                <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
                  <span style={{ width: 8, height: 2, borderRadius: 1, background: T.danger, display: "inline-block" }} />
                  Negativas: <b style={{ marginLeft: 2, fontFamily: T.mono }}>{d.negativas}</b>
                </span>
              )}
              {d.pendentes > 0 && (
                <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.accent }}>
                  <span style={{ width: 8, height: 0, borderTop: `2px dashed ${T.accent}`, display: "inline-block" }} />
                  Projeção: <b style={{ marginLeft: 2, fontFamily: T.mono }}>{d.ativos + d.pendentes}</b>
                  <span style={{ color: T.ink4, fontSize: 11 }}>(ativos + pendentes)</span>
                </span>
              )}
            </div>
            {!isDaily && d.partners && (() => {
              const sorted = [...d.partners].filter(p => p.ativos > 0).sort((a,b) => b.ativos - a.ativos);
              return (<>
                {sorted.slice(0, 6).map((p, pi) => (
                  <div key={pi} style={{ display: "flex", justifyContent: "space-between", gap: 18, fontSize: 11.5, color: T.ink2, marginBottom: 2 }}>
                    <span>{p.name}</span>
                    <span style={{ fontFamily: T.mono, color: T.ink }}>{p.ativos}</span>
                  </div>
                ))}
                {sorted.length > 6 && (
                  <div style={{ fontSize: 11, color: T.ink4, marginTop: 3 }}>+{sorted.length - 6} mais — clique para ver</div>
                )}
              </>);
            })()}
          </div>
        );
      })()}
    </div>
  );
}

// ── Animated rank bar ─────────────────────────────────────────────────────────
function AnimBar({ width, background, borderRadius = "inherit" }) {
  const [w, setW] = useState("0%");
  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setW(width)));
    return () => cancelAnimationFrame(id);
  }, [width]);
  return <div style={{ height:"100%", width:w, background, borderRadius, transition:"width 0.55s cubic-bezier(0.16,1,0.3,1)" }} />;
}

// ── Rank list ─────────────────────────────────────────────────────────────────
function RankList({ items, suffixKey = "count", pctKey }) {
  const max = Math.max(...items.map(i => i[suffixKey]), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "20px 1fr auto", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${T.hair2}`, fontSize: 13 }}>
          <span style={{ fontFamily: T.mono, fontSize: 11, color: T.ink3 }}>{String(i + 1).padStart(2, "0")}</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</span>
            <div style={{ height: 3, background: T.surface2, borderRadius: 999, overflow: "hidden" }}>
              <AnimBar width={`${(item[suffixKey] / max) * 100}%`} background={`linear-gradient(90deg,${T.accent},#2a4a8f)`} />
            </div>
          </div>
          <div style={{ fontFamily: T.mono, fontSize: 12, fontVariantNumeric: "tabular-nums", color: T.ink, display: "flex", gap: 6, alignItems: "baseline" }}>
            <span>{item[suffixKey]}</span>
            {pctKey && item[pctKey] && <span style={{ fontSize: 11, color: T.ok }}>{item[pctKey]}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Segmented control ─────────────────────────────────────────────────────────
function Seg({ options, value, onChange }) {
  return (
    <div style={{ display: "inline-flex", background: "#EFECE5", borderRadius: 8, padding: 3, gap: 2 }}>
      {options.map(o => (
        <button key={o.v} onClick={() => onChange(o.v)}
          style={{ border: 0, fontSize: 11.5, fontWeight: 500, padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", background: value === o.v ? T.surface : "transparent", color: value === o.v ? T.ink : T.ink3, boxShadow: value === o.v ? "0 1px 2px rgba(0,0,0,.05)" : "none" }}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ── Card primitives ───────────────────────────────────────────────────────────
const cardStyle    = { background: T.surface, backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)", border: `1px solid ${T.hair}`, borderRadius: T.r, boxShadow: T.shadowMd, position: "relative" };
const cardHeadStyle= { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${T.hair}` };
const cardTitleStyle={ fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em", color: T.ink, margin: 0, display: "flex", alignItems: "center", gap: 8 };

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const IcoSearch   = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"   strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="6.5"/><path d="m20 20-4-4"/></svg>;
const IcoCalendar = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="3.5" y="5" width="17" height="15" rx="2.5"/><path d="M3.5 10h17M8 3v4M16 3v4"/></svg>;
const IcoBolt     = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M13 3 4 14h7l-1 7 9-11h-7l1-7Z"/></svg>;
const IcoAlert    = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M12 4 2.5 20h19L12 4Z"/><path d="M12 10v5M12 18v.5"/></svg>;
const IcoFilter   = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 5h18l-7 9v6l-4-2v-4L3 5Z"/></svg>;

// ── Main component ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(false);
  const [ready,   setReady]     = useState(false);
  const [err, setErr]           = useState("");
  const [showCondoModal, setShowCondoModal]     = useState(false);
  const [condoModalSort, setCondoModalSort]     = useState("count");
  const [monthModal, setMonthModal]             = useState(null);
  const [chartView, setChartView]               = useState("monthly");
  const [showExpiringModal, setShowExpiringModal] = useState(false);
  const [partnersView, setPartnersView]         = useState("30d");
  const [hovPartnerIdx, setHovPartnerIdx]       = useState(null);
  const [condosView, setCondosView]             = useState(12);
  const [lastFetch, setLastFetch]               = useState(null);
  const [metrics, setMetrics] = useState({ todayChecks: 0, yesterdayChecks: 0, monthChecks: 0, prevMonthChecks: 0, dailyChecks30: [], monthlyChecks12: [], topPartners: [], topCondos: [], lastUpdate: null });
  const [items, setItems]                       = useState([]);
  const [recentActivations, setRecentActivations] = useState([]);
  const [condoUnitsMap, setCondoUnitsMap]       = useState({});
  const [negativesList, setNegativesList]       = useState([]);
  const [partnerColorMap, setPartnerColorMap]   = useState({});
  const [activationsLimit, setActivationsLimit] = useState(10);
  const [activationsSort, setActivationsSort]   = useState({ field: "createdAt", direction: "desc" });
  const [limit, setLimit]                       = useState(15);

  const token = useMemo(() => localStorage.getItem("token") || "", []);

  useEffect(() => { if (!token) navigate("/", { replace: true }); }, [token, navigate]);

  // ── Load data ──────────────────────────────────────────────────────────────
  async function loadAll(currentLimit = limit) {
    setErr("");
    if (!API_URL) { setErr("API_URL não definida"); return; }
    if (!token)   { setErr("Sem token. Faça login novamente."); return; }
    setLoading(true);

    const h = { Authorization: `Bearer ${token}` };

    const applyCondoUnits = (cuData) => {
      if (!Array.isArray(cuData.condos) || !cuData.condos.length) return;
      const norm = s => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "");
      const idMap = {}, nameMap = {}, normMap = {};
      cuData.condos.forEach(({ id, name, totalUnits }) => {
        if (!totalUnits) return;
        if (id)   idMap[id]       = totalUnits;
        if (name) { nameMap[name] = totalUnits; normMap[norm(name)] = totalUnits; }
      });
      setCondoUnitsMap({ idMap, nameMap, normMap });
    };

    try {
      // Fetch all in parallel — eliminates sequential blocking
      const [mRes, cRes, aRes, nRes] = await Promise.all([
        fetch(`${API_URL}/admin/metrics`,                             { headers: h }),
        fetch(`${API_URL}/admin/checks/recent?limit=${currentLimit}`, { headers: h }),
        fetch(`${API_URL}/admin/activations?limit=5000&period=all`,   { headers: h }),
        fetch(`${API_URL}/admin/negatives`,                           { headers: h }),
      ]);

      // Check auth on critical requests
      if (mRes.status === 401 || mRes.status === 403 || cRes.status === 401 || cRes.status === 403) {
        await logout({ remote: false });
        navigate("/", { replace: true }); return;
      }

      const [mData, cData, aData, nData] = await Promise.all([
        mRes.json(), cRes.json(), aRes.json(), nRes.json(),
      ]);

      if (!mRes.ok) throw new Error(mData?.message || "Falha ao carregar métricas");
      if (!cRes.ok) throw new Error(cData?.message || "Falha ao carregar consultas");

      setMetrics({ todayChecks: Number(mData.todayChecks || 0), yesterdayChecks: Number(mData.yesterdayChecks || 0), monthChecks: Number(mData.monthChecks || 0), prevMonthChecks: Number(mData.prevMonthChecks || 0), dailyChecks30: mData.dailyChecks30 || [], monthlyChecks12: mData.monthlyChecks12 || [], topPartners: mData.topPartners || [], topCondos: mData.topCondos || [], lastUpdate: mData.lastUpdate || null });
      setItems(Array.isArray(cData.items) ? cData.items : []);
      setRecentActivations(Array.isArray(aData.items) ? aData.items : []);
      setNegativesList(Array.isArray(nData.items) ? nData.items : []);
      setLastFetch(new Date());

      // Partner colors in background — enriches badges after main data loads
      fetch(`${API_URL}/admin/partners?limit=500`, { headers: h })
        .then(r => r.ok ? r.json() : null)
        .then(pData => {
          if (!pData?.items) return;
          const map = {};
          pData.items.forEach(p => { if (p.company && p.color) map[p.company] = p.color; });
          setPartnerColorMap(map);
        })
        .catch(() => {});

      // Condo units in background — non-blocking, enriches UI after main data loads
      fetch(`${API_URL}/admin/superlogica/condo-units-by-name`, { headers: h })
        .then(r => r.ok ? r.json() : null)
        .then(cuData => { if (cuData) applyCondoUnits(cuData); })
        .catch(() => {});

    } catch (e) { setErr(e?.message || "Erro ao carregar dados"); }
    finally     { setLoading(false); setReady(true); }
  }
  useEffect(() => { loadAll(15); }, []);

  // ── Date helpers ───────────────────────────────────────────────────────────
  function parseDateUtc(v) {
    if (!v) return null;
    if (typeof v === "string") {
      const t = v.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) { const [y,m,d] = t.split("-").map(Number); return new Date(Date.UTC(y,m-1,d)); }
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(t)) { const [d,m,y] = t.split("/").map(Number); return new Date(Date.UTC(y,m-1,d)); }
    }
    const ms = Date.parse(v);
    return isNaN(ms) ? null : new Date(ms);
  }
  function actDate(act) { return parseDateUtc(act.activationDate) || parseDateUtc(act.activationDateFormatted) || parseDateUtc(act.createdAt); }

  // ── Derived data ───────────────────────────────────────────────────────────
  const monthlyActivations12 = useMemo(() => {
    const now = new Date(), months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      months.push({ key: `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`, label: d.toLocaleDateString("pt-BR",{month:"short",year:"2-digit",timeZone:"UTC"}).replace(". ","/"), ativos: 0, pendentes: 0 });
    }
    recentActivations.forEach(act => {
      if (act.disabledAt) return;
      const s = String(act.status||"").toUpperCase();
      if (s !== "ATIVADO" && s !== "PENDENTE") return;
      const d = actDate(act); if (!d||isNaN(d)) return;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`;
      const m = months.find(x => x.key === key);
      if (m) s === "ATIVADO" ? m.ativos++ : m.pendentes++;
    });
    return months.map(m => ({...m, count: m.ativos + m.pendentes}));
  }, [recentActivations]);

  const monthlyNegatives12 = useMemo(() => {
    const map = {};
    negativesList.forEach(neg => {
      const d = parseDateUtc(neg.createdAt); if (!d||isNaN(d)) return;
      const k = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`;
      map[k] = (map[k]||0)+1;
    });
    recentActivations.forEach(act => {
      if (act.disabledAt) return;
      if (String(act.status||"").toUpperCase() !== "NEGATIVA") return;
      const d = parseDateUtc(act.createdAt); if (!d||isNaN(d)) return;
      const k = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`;
      map[k] = (map[k]||0)+1;
    });
    return map;
  }, [negativesList, recentActivations]);

  const dailyActivations30 = useMemo(() => {
    const now = new Date(), days = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
      days.push({ key: `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}-${String(d.getUTCDate()).padStart(2,"0")}`, label: `${String(d.getUTCDate()).padStart(2,"0")}/${String(d.getUTCMonth()+1).padStart(2,"0")}`, ativos: 0, pendentes: 0 });
    }
    recentActivations.forEach(act => {
      if (act.disabledAt) return;
      const s = String(act.status||"").toUpperCase();
      if (s !== "ATIVADO" && s !== "PENDENTE") return;
      const d = actDate(act); if (!d||isNaN(d)) return;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}-${String(d.getUTCDate()).padStart(2,"0")}`;
      const it = days.find(x => x.key === key);
      if (it) s === "ATIVADO" ? it.ativos++ : it.pendentes++;
    });
    return days.map(d => ({...d, count: d.ativos + d.pendentes}));
  }, [recentActivations]);

  const dailyNegatives30 = useMemo(() => {
    const map = {};
    negativesList.forEach(neg => {
      const d = parseDateUtc(neg.createdAt); if (!d||isNaN(d)) return;
      const k = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}-${String(d.getUTCDate()).padStart(2,"0")}`;
      map[k] = (map[k]||0)+1;
    });
    recentActivations.forEach(act => {
      if (act.disabledAt) return;
      if (String(act.status||"").toUpperCase() !== "NEGATIVA") return;
      const d = parseDateUtc(act.createdAt); if (!d||isNaN(d)) return;
      const k = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}-${String(d.getUTCDate()).padStart(2,"0")}`;
      map[k] = (map[k]||0)+1;
    });
    return map;
  }, [negativesList, recentActivations]);

  const monthlyDetail12 = useMemo(() => {
    const map = {};
    recentActivations.forEach(act => {
      if (act.disabledAt) return;
      const s = String(act.status||"").toUpperCase();
      if (s !== "ATIVADO" && s !== "PENDENTE") return;
      const d = actDate(act); if (!d||isNaN(d)) return;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`;
      const partner = act.partnerCompany || act.provider || "Desconhecido";
      const condo   = act.condo || act.condoName || "Desconhecido";
      if (!map[key]) map[key] = {};
      if (!map[key][partner]) map[key][partner] = { ativos: 0, pendentes: 0, condos: {} };
      s === "ATIVADO" ? map[key][partner].ativos++ : map[key][partner].pendentes++;
      map[key][partner].condos[condo] = (map[key][partner].condos[condo]||0)+1;
    });
    const out = {};
    Object.entries(map).forEach(([k, partners]) => {
      out[k] = Object.entries(partners).sort((a,b)=>(b[1].ativos+b[1].pendentes)-(a[1].ativos+a[1].pendentes)).map(([name,data])=>({ name, ativos:data.ativos, pendentes:data.pendentes, count:data.ativos+data.pendentes, condos:Object.entries(data.condos).sort((a,b)=>b[1]-a[1]).map(([n,c])=>({name:n,count:c})) }));
    });
    return out;
  }, [recentActivations]);

  function viewSince(view) {
    const now = new Date();
    if (view === "30d") return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (view - 1), 1));
  }

  function viewPrevSince(view) {
    const now = new Date();
    if (view === "30d") return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (view * 2 - 1), 1));
  }

  const topPartners12 = useMemo(() => {
    const since = viewSince(partnersView);
    const prevSinceDate = viewPrevSince(partnersView);
    const cur = {}, prev = {};
    recentActivations.forEach(act => {
      if (act.disabledAt) return;
      const status = String(act.status||"").toUpperCase();
      if (status !== "ATIVADO" && status !== "PENDENTE") return;
      const d = actDate(act); if (!d||isNaN(d)) return;
      const n = act.partnerCompany || act.provider || ""; if (!n) return;
      if (d >= since) {
        if (!cur[n]) cur[n] = { ativos:0, pendentes:0 };
        if (status === "ATIVADO") cur[n].ativos++;
        else cur[n].pendentes++;
      } else if (d >= prevSinceDate && status === "ATIVADO") {
        prev[n] = (prev[n]||0)+1;
      }
    });
    return Object.entries(cur)
      .sort((a,b)=>b[1].ativos-a[1].ativos)
      .slice(0,8)
      .map(([name,data]) => {
        const prevCount = prev[name]||0;
        const delta = prevCount > 0 ? Math.round(((data.ativos-prevCount)/prevCount)*100) : null;
        return { name, count: data.ativos, pendentes: data.pendentes, delta };
      });
  }, [recentActivations, partnersView]);

  const topCondos12 = useMemo(() => {
    const since = viewSince(condosView);
    const map = {};
    const norm = s => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g,"").replace(/[^a-z0-9]/g,"");
    const { idMap={}, nameMap={}, normMap={} } = condoUnitsMap;
    const getUnits = act => { if (act.condoId&&idMap[act.condoId]) return idMap[act.condoId]; const n=act.condo||act.condoName||""; return nameMap[n]||normMap[norm(n)]||null; };
    recentActivations.forEach(act => {
      if (act.disabledAt || String(act.status||"").toUpperCase() !== "ATIVADO") return;
      const d = actDate(act); if (!d||isNaN(d)||d<since) return;
      const n = act.condo||act.condoName||""; if (!n) return;
      if (!map[n]) map[n] = { count:0, totalUnits:getUnits(act) };
      map[n].count++;
      if (!map[n].totalUnits) map[n].totalUnits = getUnits(act);
    });
    return Object.entries(map).sort((a,b)=>b[1].count-a[1].count).slice(0,8).map(([name,data])=>({name,count:data.count,totalUnits:data.totalUnits}));
  }, [recentActivations, condosView, condoUnitsMap]);

  const topCondosAll = useMemo(() => {
    const since = viewSince(condosView);
    const map = {};
    const norm = s => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g,"").replace(/[^a-z0-9]/g,"");
    const { idMap={}, nameMap={}, normMap={} } = condoUnitsMap;
    const getUnits = act => { if (act.condoId&&idMap[act.condoId]) return idMap[act.condoId]; const n=act.condo||act.condoName||""; return nameMap[n]||normMap[norm(n)]||null; };
    recentActivations.forEach(act => {
      if (act.disabledAt || String(act.status||"").toUpperCase() !== "ATIVADO") return;
      const d = actDate(act); if (!d||isNaN(d)||d<since) return;
      const n = act.condo||act.condoName||""; if (!n) return;
      if (!map[n]) map[n] = { count:0, totalUnits:getUnits(act) };
      map[n].count++;
      if (!map[n].totalUnits) map[n].totalUnits = getUnits(act);
    });
    return Object.entries(map).sort((a,b)=>b[1].count-a[1].count).slice(0,50).map(([name,data])=>({name,count:data.count,totalUnits:data.totalUnits}));
  }, [recentActivations, condosView, condoUnitsMap]);

  const expiringContracts = useMemo(() => {
    const now = new Date(), todayUTC = Date.UTC(now.getUTCFullYear(),now.getUTCMonth(),now.getUTCDate()), winEnd = todayUTC + 10*86400000, res = [];
    recentActivations.forEach(act => {
      if (act.disabledAt || String(act.status||"").toUpperCase() !== "ATIVADO") return;
      const cr = parseDateUtc(act.createdAt); if (!cr||isNaN(cr)) return;
      const ann = new Date(Date.UTC(cr.getUTCFullYear()+1,cr.getUTCMonth(),cr.getUTCDate()));
      if (ann.getTime()>=todayUTC && ann.getTime()<=winEnd) {
        res.push({ id:act._id||act.id, clientName:act.clientName||act.document||"—", partner:act.partnerCompany||act.provider||"—", condo:act.condo||act.condoName||"—", createdAt:cr, anniversary:ann, daysLeft:Math.round((ann.getTime()-todayUTC)/86400000) });
      }
    });
    return res.sort((a,b)=>a.daysLeft-b.daysLeft);
  }, [recentActivations]);

  // Partner custom color map: loaded from partners list in background
  const partnerColors = partnerColorMap;

  // KPI derived
  const totalAtivosAtivos = useMemo(() => recentActivations.filter(a=>!a.disabledAt&&String(a.status||"").toUpperCase()==="ATIVADO").length, [recentActivations]);
  const totalPagos        = useMemo(() => recentActivations.filter(a=>!a.disabledAt&&String(a.status||"").toUpperCase()==="ATIVADO"&&a.paidAt).length, [recentActivations]);
  const sparkAtivacoes    = useMemo(() => monthlyActivations12.map(m=>m.ativos), [monthlyActivations12]);

  // Check KPI sparklines and deltas (from metrics API)
  const sparkToday  = useMemo(() => metrics.dailyChecks30.map(d => d.count), [metrics.dailyChecks30]);
  const sparkMonth  = useMemo(() => metrics.monthlyChecks12.map(d => d.count), [metrics.monthlyChecks12]);
  const deltaToday  = metrics.yesterdayChecks > 0 ? Math.round(((metrics.todayChecks - metrics.yesterdayChecks) / metrics.yesterdayChecks) * 100) : null;
  const deltaMonth  = metrics.prevMonthChecks > 0 ? Math.round(((metrics.monthChecks - metrics.prevMonthChecks) / metrics.prevMonthChecks) * 100) : null;

  // Chart data merged with negatives
  const chartData = useMemo(() => {
    const dataset = chartView === "monthly" ? monthlyActivations12 : dailyActivations30;
    const negMap  = chartView === "monthly" ? monthlyNegatives12   : dailyNegatives30;
    return dataset.map(m => ({ ...m, negativas: negMap[m.key]||0, partners: monthlyDetail12[m.key]||[] }));
  }, [chartView, monthlyActivations12, dailyActivations30, monthlyNegatives12, dailyNegatives30, monthlyDetail12]);

  // Activations sort
  function sortActivations(list) {
    const { field, direction } = activationsSort;
    return [...list].sort((a, b) => {
      let va = a[field]||"", vb = b[field]||"";
      if (field==="createdAt"||field==="activatedAt") { va=va?new Date(va):0; vb=vb?new Date(vb):0; } else { va=va.toString().toLowerCase(); vb=vb.toString().toLowerCase(); }
      return va<vb ? (direction==="asc"?-1:1) : va>vb ? (direction==="asc"?1:-1) : 0;
    });
  }
  function handleActivationsSort(field) {
    setActivationsSort(prev => ({ field, direction: prev.field===field ? (prev.direction==="asc"?"desc":"asc") : "asc" }));
  }

  const lastFetchText = lastFetch ? new Date(lastFetch).toLocaleTimeString("pt-BR") : "—";
  const handleShowMore = async () => { setLimit(50);  await loadAll(50);  };
  const handleShowLess = async () => { setLimit(15);  await loadAll(15);  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const fa = (delay = 0) => ({ animation: `dash-fadeUp 0.45s ${delay}ms ease both` });

  return (
    <div style={{ fontFamily: T.font }}>

      {/* ── Page head ────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, marginBottom: 24, ...fa(0) }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.025em", margin: "0 0 4px", lineHeight: 1.1, color: T.ink }}>Dashboard</h1>
          <div style={{ fontSize: 13, color: T.ink3, display: "flex", alignItems: "center", gap: 8 }}>
            {lastFetch && <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.ok, boxShadow: `0 0 0 3px ${T.okSoft}`, display: "inline-block", animation: "dash-pulse 2.4s ease-in-out infinite" }} />}
            {lastFetch ? `Atualizado às ${lastFetchText}` : "Carregando…"}
          </div>
        </div>

      </div>

      {err && <div style={{ background:T.dangerSoft, border:`1px solid rgba(197,34,31,.2)`, borderRadius:12, padding:"12px 16px", marginBottom:20, color:T.danger, fontSize:14 }}>{err}</div>}

      {/* ── KPI grid ─────────────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:18, ...fa(60) }}>
        <KPI
          label="Consultas hoje" value={metrics.todayChecks}
          sub="vs ontem"
          delta={deltaToday !== null ? `${deltaToday >= 0 ? "+" : ""}${deltaToday}%` : undefined}
          deltaDir={deltaToday === null ? undefined : deltaToday >= 0 ? "up" : "down"}
          iconEl={IcoSearch} iconTone="accent"
          spark={sparkToday} sparkColor={T.accent}
        />
        <KPI
          label="Consultas no mês" value={metrics.monthChecks}
          sub="vs mês anterior"
          delta={deltaMonth !== null ? `${deltaMonth >= 0 ? "+" : ""}${deltaMonth}%` : undefined}
          deltaDir={deltaMonth === null ? undefined : deltaMonth >= 0 ? "up" : "down"}
          iconEl={IcoCalendar} iconTone="accent"
          spark={sparkMonth} sparkColor={T.accent}
        />
        <KPI
          label="Contratos a vencer" value={expiringContracts.length}
          sub={expiringContracts.length > 0 ? `${expiringContracts.length} em até 10 dias` : "Nenhum nos próximos 10 dias"}
          iconEl={<ExclamationTriangleIcon style={{width:15,height:15}}/>}
          iconTone={expiringContracts.length > 0 ? "warn" : "def"}
          onClick={() => setShowExpiringModal(true)}
        />
        <KPI
          label="Ativações ativas" value={totalAtivosAtivos}
          sub={totalPagos > 0 ? `acumulado total · ${totalPagos} paga${totalPagos === 1 ? "" : "s"}` : "acumulado total"}
          iconEl={IcoBolt} iconTone="ok"
          spark={sparkAtivacoes} sparkColor={T.ok}
        />
      </div>

      {/* ── Chart + top condos mês ────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"1.6fr 1fr", gap:14, marginBottom:14, ...fa(120) }}>

        {/* Bar chart */}
        <div style={cardStyle}>
          <div style={cardHeadStyle}>
            <h3 style={cardTitleStyle}>
              Ativações por {chartView==="monthly"?"mês":"dia"}
              <span style={{ fontWeight:400, color:T.ink3 }}>· ativos / pendentes / negativas</span>
            </h3>
            <Seg value={chartView} onChange={v=>{setChartView(v);}} options={[{v:"monthly",l:"12 meses"},{v:"daily",l:"30 dias"}]} />
          </div>
          <div style={{ paddingTop: 8, paddingBottom: 4 }}>
            <LineChart data={chartData} isDaily={chartView==="daily"} onClickBar={d => setMonthModal({ key:d.key, label:d.label, ativos:d.ativos, pendentes:d.pendentes, negativas:d.negativas||0, partners:d.partners||[] })} />
            <div style={{ display:"flex", gap:18, marginTop:4, marginBottom:16, padding:"0 20px", fontSize:11.5, color:T.ink3 }}>
              {[[T.accent,"Ativos"],[PEND_C,"Pendentes"],[T.danger,"Negativas"]].map(([c,l])=>(
                <span key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ width:16, height:2, borderRadius:1, background:c, display:"inline-block" }} />{l}
                </span>
              ))}
              {chartData.some(d=>d.pendentes>0) && (
                <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ width:16, height:0, borderTop:`2px dashed ${T.accent}`, opacity:0.55, display:"inline-block" }} />Projeção
                </span>
              )}
              <span style={{ marginLeft:"auto", color:T.ink3 }}>Total · <b style={{ fontFamily:T.mono, color:T.ink }}>{chartData.reduce((s,d)=>s+d.count,0)}</b></span>
            </div>
          </div>
        </div>

        {/* Top condos mês (por consultas) */}
        <div style={cardStyle}>
          <div style={cardHeadStyle}>
            <h3 style={cardTitleStyle}>Top condomínios <span style={{ fontWeight:400, color:T.ink3 }}>· consultas · mês</span></h3>
            <span role="button" style={{ fontSize:12, color:T.accent, cursor:"pointer" }} onClick={()=>setShowCondoModal(true)}>Ver todos →</span>
          </div>
          <div style={{ padding:"8px 20px 16px" }}>
            {metrics.topCondos.length===0 ? <div style={{ fontSize:13, color:T.ink4 }}>Sem dados</div> : (
              <RankList items={metrics.topCondos.slice(0,6).map(c=>({ name:c.condoName, count:c.totalChecks }))} suffixKey="count" />
            )}
          </div>
        </div>
      </div>

      {/* ── Top parceiros + top condos ativações ─────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"1.6fr 1fr", gap:14, marginBottom:18, ...fa(180) }}>

        {/* Top parceiros */}
        <div style={cardStyle}>
          <div style={cardHeadStyle}>
            <h3 style={cardTitleStyle}>Top parceiros <span style={{ fontWeight:400, color:T.ink3 }}>· {partnersView==="30d"?"mês":`${partnersView}m`}</span></h3>
            <Seg value={partnersView} onChange={setPartnersView} options={[{v:"30d",l:"mês"},{v:3,l:"3m"},{v:6,l:"6m"},{v:12,l:"12m"}]} />
          </div>
          <div style={{ padding:"8px 20px 16px" }}>
            {topPartners12.length===0 ? (
              <div style={{ fontSize:13, color:T.ink4 }}>Sem dados</div>
            ) : (() => {
              const maxVal = Math.max(...topPartners12.map(p => p.count + (p.pendentes||0)), 1);
              const periodLabel = partnersView === "30d" ? "mês" : `${partnersView}m`;
              return (
                <div style={{ display:"flex", flexDirection:"column" }}>
                  {topPartners12.map((item, i) => {
                    const pm = provMark(item.name);
                    const deltaDir = item.delta === null ? null : item.delta >= 0 ? "up" : "down";
                    const ds = { up:{ color:T.ok, bg:T.okSoft }, down:{ color:T.danger, bg:T.dangerSoft } }[deltaDir];
                    const isHov = hovPartnerIdx === i;
                    return (
                      <div key={item.name}
                        onMouseEnter={() => setHovPartnerIdx(i)}
                        onMouseLeave={() => setHovPartnerIdx(null)}
                        style={{ display:"grid", gridTemplateColumns:"20px 26px 1fr auto", alignItems:"center", gap:10, padding:"9px 0", borderBottom:`1px solid ${T.hair2}`, fontSize:13, position:"relative", background: isHov ? T.surface2 : "transparent", transition:"background 120ms", cursor:"default" }}>
                        <span style={{ fontFamily:T.mono, fontSize:11, color:T.ink3 }}>{String(i+1).padStart(2,"0")}</span>
                        <div style={{ width:26, height:26, borderRadius:7, background: partnerColors[item.name] || pm.c, color:"#fff", display:"grid", placeItems:"center", fontSize:10, fontWeight:700, flexShrink:0 }}>{pm.l}</div>
                        <div style={{ minWidth:0 }}>
                          <span style={{ whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", display:"block", color:T.ink }}>{item.name}</span>
                          <div style={{ height:4, background:T.surface2, borderRadius:999, marginTop:3, overflow:"hidden", position:"relative" }}>
                            <div style={{ position:"absolute", top:0, left:0, height:"100%", width:`${((item.count+(item.pendentes||0))/maxVal)*100}%`, background:T.accentSoft, borderRadius:"inherit", transition:"width 0.65s cubic-bezier(0.34,1.15,0.64,1)" }} />
                            <div style={{ position:"absolute", top:0, left:0, height:"100%", width:"100%", overflow:"hidden" }}>
                              <AnimBar width={`${(item.count/maxVal)*100}%`} background={`linear-gradient(90deg,${T.accent},#2a4a8f)`} />
                            </div>
                          </div>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                          <span style={{ fontFamily:T.mono, fontSize:12, fontVariantNumeric:"tabular-nums", color:T.ink }}>{item.count}</span>
                          {item.pendentes > 0 && (
                            <span style={{ fontSize:11, fontWeight:500, padding:"2px 6px", borderRadius:999, color:T.accent, background:T.accentSoft }}>+{item.pendentes}</span>
                          )}
                          {ds && item.delta !== null && (
                            <span style={{ fontSize:11, fontWeight:500, padding:"2px 6px", borderRadius:999, color:ds.color, background:ds.bg }}>
                              {item.delta >= 0 ? "+" : ""}{item.delta}%
                            </span>
                          )}
                        </div>
                        {isHov && (
                          <div style={{ position:"absolute", right:0, top:"calc(100% + 4px)", zIndex:200, background:T.surface, border:`1px solid ${T.hair}`, borderRadius:10, padding:"10px 14px", fontSize:12, boxShadow:"0 8px 24px -4px rgba(20,20,22,0.18)", whiteSpace:"nowrap", pointerEvents:"none", minWidth:200 }}>
                            <div style={{ fontWeight:600, fontSize:12.5, marginBottom:7, color:T.ink }}>{item.name}</div>
                            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", gap:20 }}>
                                <span style={{ color:T.ink2 }}>Ativos</span>
                                <b style={{ fontFamily:T.mono, color:T.ink }}>{item.count}</b>
                              </div>
                              {item.pendentes > 0 && (
                                <div style={{ display:"flex", justifyContent:"space-between", gap:20 }}>
                                  <span style={{ color:T.ink2 }}>Pendentes</span>
                                  <b style={{ fontFamily:T.mono, color:T.accent }}>+{item.pendentes}</b>
                                </div>
                              )}
                              {item.delta !== null && (
                                <div style={{ display:"flex", justifyContent:"space-between", gap:20, paddingTop:5, marginTop:2, borderTop:`1px solid ${T.hair2}` }}>
                                  <span style={{ color:T.ink2 }}>Var. ativos vs {periodLabel} ant.</span>
                                  <b style={{ fontFamily:T.mono, color: item.delta >= 0 ? T.ok : T.danger }}>{item.delta >= 0 ? "+" : ""}{item.delta}%</b>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:10, marginTop:2 }}>
                    <span style={{ fontSize:12, color:T.ink3 }}>Total</span>
                    <span style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontFamily:T.mono, fontSize:13, fontWeight:600, color:T.ink, fontVariantNumeric:"tabular-nums" }}>
                        {topPartners12.reduce((s, p) => s + p.count, 0)}
                      </span>
                      {topPartners12.some(p => p.pendentes > 0) && (
                        <span style={{ fontSize:11, fontWeight:500, padding:"2px 7px", borderRadius:999, color:T.accent, background:T.accentSoft, fontFamily:T.mono, fontVariantNumeric:"tabular-nums" }}>
                          +{topPartners12.reduce((s, p) => s + (p.pendentes||0), 0)} pend.
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Top condos ativações */}
        <div style={cardStyle}>
          <div style={cardHeadStyle}>
            <h3 style={cardTitleStyle}>Top condomínios geral <span style={{ fontWeight:400, color:T.ink3 }}>· {condosView==="30d"?"mês":`${condosView}m`}</span></h3>
            <Seg value={condosView} onChange={setCondosView} options={[{v:"30d",l:"mês"},{v:3,l:"3m"},{v:6,l:"6m"},{v:12,l:"12m"}]} />
          </div>
          <div style={{ padding:"8px 20px 16px" }}>
            {topCondos12.length===0 ? <div style={{ fontSize:13, color:T.ink4 }}>Sem dados</div> : (() => {
              const maxVal = topCondos12[0]?.count||1;
              return (
                <div style={{ display:"flex", flexDirection:"column" }}>
                  {topCondos12.map((item, i) => {
                    const pct      = item.totalUnits ? Math.round((item.count/item.totalUnits)*100) : null;
                    const barW     = item.totalUnits ? `${Math.min((item.count/item.totalUnits)*100,100)}%` : `${(item.count/maxVal)*100}%`;
                    const barColor = pct===null?T.accent:pct>=80?T.ok:pct>=40?T.warn:T.accent;
                    return (
                      <div key={item.name} style={{ padding:"9px 0", borderBottom:`1px solid ${T.hair2}`, display:"grid", gridTemplateColumns:"20px 1fr auto", gap:10, alignItems:"center" }}>
                        <span style={{ fontFamily:T.mono, fontSize:11, color:T.ink3 }}>{String(i+1).padStart(2,"0")}</span>
                        <div>
                          <span style={{ fontSize:13, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", display:"block" }}>{item.name}</span>
                          <div style={{ height:3, background:T.surface2, borderRadius:999, marginTop:3, overflow:"hidden" }}><AnimBar width={barW} background={barColor} /></div>
                        </div>
                        <div style={{ fontFamily:T.mono, fontSize:12, fontVariantNumeric:"tabular-nums", textAlign:"right" }}>
                          {item.totalUnits ? (
                            <><span style={{ fontWeight:600 }}>{item.count}/{item.totalUnits}</span><span style={{ fontSize:11, marginLeft:4, color:barColor }}> {pct}%</span></>
                          ) : <span style={{ color:T.ink3 }}>{item.count}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Últimas ativações ────────────────────────────────────────────── */}
      <div style={{ ...cardStyle, marginBottom:14, ...fa(240) }}>
        <div style={cardHeadStyle}>
          <h3 style={cardTitleStyle}>
            Últimas ativações
            <span style={{ fontWeight:400, color:T.ink3 }}>· {Math.min(activationsLimit, recentActivations.length)} registros</span>
          </h3>
          <button onClick={()=>navigate("/admin/ativacoes/lista")} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:8, border:`1px solid ${T.hair}`, background:T.surface, fontSize:12, fontWeight:500, cursor:"pointer", color:T.ink2, fontFamily:"inherit", boxShadow:T.shadowSm }}>
            Ver todas →
          </button>
        </div>
        {recentActivations.length===0 ? (
          <div style={{ padding:20, color:T.ink4, fontSize:13 }}>Nenhuma ativação encontrada.</div>
        ) : (
          <>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr>
                    {[["clientName","Cliente"],["condo","Condomínio"],["provider","Provedora"],["plan","Plano"],["status","Status"],["createdAt","Criado em"],["activatedAt","Ativado em"]].map(([k,l])=>(
                      <th key={k} onClick={()=>handleActivationsSort(k)} style={{ textAlign:"left", fontWeight:500, fontSize:11, color:T.ink3, letterSpacing:"0.04em", textTransform:"uppercase", padding:"12px 16px", borderBottom:`1px solid ${T.hair}`, background:"transparent", cursor:"pointer" }}>{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortActivations(recentActivations).slice(0,activationsLimit).map((act,idx)=>{
                    const st  = activationStatus(act);
                    const av  = statusAvatar(act);
                    const pm  = provMark(act.provider||"");
                    return (
                      <tr key={idx} style={{ borderBottom:`1px solid ${T.hair2}` }}>
                        <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <div style={{ width:28, height:28, borderRadius:"50%", background:av.bg, color:av.color, display:"grid", placeItems:"center", fontSize:10.5, fontWeight:600, flexShrink:0 }}>{initials(act.clientName||"")}</div>
                            <div>
                              <div style={{ fontWeight:500, color:T.ink }}>{toTitleCase(act.clientName)||"—"}</div>
                              <div style={{ fontSize:11.5, color:T.ink3, fontFamily:T.mono, marginTop:1 }}>{act.document||act.maskedDocument||"—"}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                          <div style={{ color:T.ink }}>{toTitleCase(act.condo||act.condoName)||"—"}</div>
                          <div style={{ fontSize:11.5, color:T.ink3, marginTop:1 }}>Unidade {act.unit||"—"}</div>
                        </td>
                        <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                          <div style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
                            <span style={{ width:22, height:22, borderRadius:6, background: act.partnerColor || partnerColors[act.partnerCompany] || pm.c, color:"#fff", display:"grid", placeItems:"center", fontSize:10, fontWeight:700 }}>{pm.l}</span>
                            {toTitleCase(act.provider)||"—"}
                          </div>
                        </td>
                        <td style={{ padding:"14px 16px", verticalAlign:"middle", color:T.ink2 }}>
                          {toTitleCase(act.plan)||"—"}
                          {act.installationDate && <div style={{ fontSize:11.5, color:T.ink3, marginTop:1 }}>Instal.: {new Date(act.installationDate).toLocaleDateString("pt-BR")}</div>}
                        </td>
                        <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                          <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:st.bg, color:st.color }}>
                            <span style={{ width:5, height:5, borderRadius:"50%", background:st.color, display:"inline-block" }} />{st.label}
                          </span>
                        </td>
                        <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                          {act.createdAt ? <><div style={{ fontWeight:500, fontVariantNumeric:"tabular-nums" }}>{new Date(act.createdAt).toLocaleDateString("pt-BR")}</div><div style={{ fontSize:11.5, color:T.ink3, marginTop:1 }}>{new Date(act.createdAt).toLocaleTimeString("pt-BR")}</div></> : "—"}
                        </td>
                        <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                          {act.activatedAt ? <><div style={{ fontWeight:500, fontVariantNumeric:"tabular-nums" }}>{new Date(act.activatedAt).toLocaleDateString("pt-BR")}</div><div style={{ fontSize:11.5, color:T.ink3, marginTop:1 }}>{new Date(act.activatedAt).toLocaleTimeString("pt-BR")}</div></> : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display:"flex", justifyContent:"center", padding:"14px 20px", borderTop:`1px solid ${T.hair2}` }}>
              {activationsLimit < recentActivations.length ? (
                <button onClick={()=>setActivationsLimit(p=>p+10)} style={{ padding:"7px 20px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface, fontSize:13, fontWeight:500, cursor:"pointer", color:T.ink2, fontFamily:"inherit", boxShadow:T.shadowSm }}>Mostrar mais</button>
              ) : activationsLimit > 10 ? (
                <button onClick={()=>setActivationsLimit(10)} style={{ padding:"7px 20px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface, fontSize:13, fontWeight:500, cursor:"pointer", color:T.ink2, fontFamily:"inherit", boxShadow:T.shadowSm }}>Mostrar menos</button>
              ) : null}
            </div>
          </>
        )}
      </div>

      {/* ── Últimas consultas ────────────────────────────────────────────── */}
      <div style={cardStyle}>
        <div style={cardHeadStyle}>
          <h3 style={cardTitleStyle}>
            Últimas consultas
            <span style={{ fontWeight:400, color:T.ink3 }}>· {Math.min(items.length, limit)} registros</span>
          </h3>
        </div>
        {loading && items.length===0 ? (
          <div style={{ textAlign:"center", padding:"40px 20px" }}><Spinner animation="border" size="sm" /><div style={{ marginTop:8, color:T.ink3, fontSize:13 }}>Carregando…</div></div>
        ) : items.length===0 ? (
          <div style={{ padding:20, color:T.ink4, fontSize:13 }}>Nenhuma consulta encontrada.</div>
        ) : (
          <>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr>
                    {["Empresa","Primeiro Nome","Condomínio","Cidade/UF","Status","Data"].map(h=>(
                      <th key={h} style={{ textAlign:"left", fontWeight:500, fontSize:11, color:T.ink3, letterSpacing:"0.04em", textTransform:"uppercase", padding:"12px 16px", borderBottom:`1px solid ${T.hair}`, background:"transparent" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} style={{ borderBottom:`1px solid ${T.hair2}` }}>
                      <td style={{ padding:"14px 16px", verticalAlign:"middle", fontWeight:500, color:T.ink }}>
                        {it.partnerId ? <span role="button" style={{ cursor:"pointer" }} onClick={()=>navigate(`/admin/partners/${it.partnerId}`)}>{toTitleCase(it.partnerCompany)||"—"}</span> : (toTitleCase(it.partnerCompany)||"—")}
                      </td>
                      <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:28, height:28, borderRadius:"50%", background:it.isClient?T.okSoft:T.dangerSoft, color:it.isClient?T.ok:T.danger, display:"grid", placeItems:"center", fontSize:10.5, fontWeight:600, flexShrink:0 }}>{initials(it.partialName||"")}</div>
                          <div>
                            <div style={{ fontWeight:500, color:T.ink }}>{it.partialName ? toTitleCase(it.partialName) : "—"}</div>
                            <div style={{ fontSize:11.5, color:T.ink3, fontFamily:T.mono, marginTop:1 }}>{it.document||it.maskedDocument||"—"}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:"14px 16px", verticalAlign:"middle", color:T.ink2 }}>
                        {toTitleCase(it.condo||it.condoName)||"—"}
                        {(it.unit||it.unidade) && <div style={{ fontSize:11.5, color:T.ink3, marginTop:1 }}>Unidade {it.unit||it.unidade}</div>}
                      </td>
                      <td style={{ padding:"14px 16px", verticalAlign:"middle", color:T.ink2 }}>{toTitleCase(it.city)||"—"}{it.state?`/${it.state.toUpperCase()}`:""}</td>
                      <td style={{ padding:"14px 16px", verticalAlign:"middle" }}>
                        <span style={{ color:it.isClient?T.ok:T.danger, fontWeight:500 }}>{it.isClient?"Encontrado":"Não encontrado"}</span>
                      </td>
                      <td style={{ padding:"14px 16px", verticalAlign:"middle", color:T.ink2, fontVariantNumeric:"tabular-nums" }}>
                        {it.createdAt ? new Date(it.createdAt).toLocaleString("pt-BR") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display:"flex", justifyContent:"center", padding:"14px 20px", borderTop:`1px solid ${T.hair2}` }}>
              {limit===15 ? (
                <button onClick={handleShowMore} disabled={loading} style={{ padding:"7px 20px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface, fontSize:13, fontWeight:500, cursor:loading?"not-allowed":"pointer", color:T.ink2, fontFamily:"inherit", opacity:loading?0.6:1, boxShadow:T.shadowSm }}>Mostrar mais</button>
              ) : (
                <button onClick={handleShowLess} disabled={loading} style={{ padding:"7px 20px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface, fontSize:13, fontWeight:500, cursor:loading?"not-allowed":"pointer", color:T.ink2, fontFamily:"inherit", opacity:loading?0.6:1, boxShadow:T.shadowSm }}>Mostrar menos</button>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Modal contratos a vencer ─────────────────────────────────────── */}
      <Modal show={showExpiringModal} onHide={()=>setShowExpiringModal(false)} size="lg" scrollable>
        <Modal.Header closeButton><Modal.Title style={{ fontSize:16, fontWeight:600, display:"flex", alignItems:"center", gap:8 }}><ExclamationTriangleIcon style={{ width:20, height:20, color:"#fbbf24" }} />Contratos a vencer — próximos 10 dias</Modal.Title></Modal.Header>
        <Modal.Body>
          {expiringContracts.length===0 ? <div style={{ color:T.ink3 }}>Nenhum contrato atingindo 1 ano nos próximos 10 dias.</div> : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ color:T.ink3, fontSize:13, marginBottom:4 }}>Ativações ativas cuja data de criação completa 1 ano em até 10 dias. Oportunidade de renovação.</div>
              {expiringContracts.map(c=>{
                const urgency   = c.daysLeft===0?"Vence hoje":c.daysLeft===1?"Vence amanhã":`Vence em ${c.daysLeft} dias`;
                const badgeColor= c.daysLeft<=2?"#dc3545":c.daysLeft<=5?"#fd7e14":"#6c757d";
                return (
                  <div key={c.id} style={{ border:`1px solid ${T.hair}`, borderRadius:12, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:14, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.clientName}</div>
                      <div style={{ fontSize:12.5, color:"#6c757d", marginBottom:1 }}>{c.partner}</div>
                      <div style={{ fontSize:12, color:"#adb5bd" }}>{c.condo}</div>
                    </div>
                    <div style={{ flexShrink:0, textAlign:"right" }}>
                      <div style={{ fontSize:12, fontWeight:600, color:badgeColor, background:badgeColor+"18", borderRadius:6, padding:"3px 8px", marginBottom:4 }}>{urgency}</div>
                      <div style={{ fontSize:11, color:"#adb5bd" }}>Criado em {c.createdAt.toLocaleDateString("pt-BR",{timeZone:"UTC"})}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={()=>setShowExpiringModal(false)}>Fechar</Button></Modal.Footer>
      </Modal>

      {/* ── Modal detalhe mês ────────────────────────────────────────────── */}
      <Modal show={!!monthModal} onHide={()=>setMonthModal(null)} size="lg" scrollable>
        <Modal.Header closeButton><Modal.Title style={{ fontSize:16, fontWeight:600 }}>Ativações — {monthModal?.label}</Modal.Title></Modal.Header>
        <Modal.Body>
          {monthModal && (
            <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
              {[["#eff6ff","#0d6efd",monthModal.ativos,"Ativo"],["#eef1f8","#4a6fa5",monthModal.pendentes,"Pendente"],["#fdf2f2","#a3272e",monthModal.negativas||0,"Negativa"],["#f8f9fa","#3a3a3c",(monthModal.ativos||0)+(monthModal.pendentes||0),"Total"]].map(([bg,color,val,label])=>val>0||label==="Total"?(
                <div key={label} style={{ background:bg, borderRadius:10, padding:"8px 16px", display:"flex", flexDirection:"column", alignItems:"center", minWidth:80 }}>
                  <span style={{ fontSize:"1.4rem", fontWeight:700, color, lineHeight:1 }}>{val}</span>
                  <span style={{ fontSize:11, color:"#6c757d", marginTop:2 }}>{label}{val!==1?"s":""}</span>
                </div>
              ):null)}
            </div>
          )}
          {!monthModal||monthModal.partners.length===0 ? <div style={{ color:T.ink3 }}>Sem dados neste mês.</div> : (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {monthModal.partners.map(p=>(
                <div key={p.name} style={{ border:`1px solid ${T.hair}`, borderRadius:12, padding:"10px 14px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <span style={{ fontWeight:600, fontSize:14 }}>{p.name}</span>
                    <div style={{ display:"flex", gap:6 }}>
                      {p.ativos>0&&<span style={{ fontSize:11.5, background:"#eff6ff", color:"#0d6efd", borderRadius:6, padding:"2px 7px", fontWeight:500 }}>{p.ativos} ativo{p.ativos!==1?"s":""}</span>}
                      {p.pendentes>0&&<span style={{ fontSize:11.5, background:"#eef1f8", color:"#4a6fa5", borderRadius:6, padding:"2px 7px", fontWeight:500 }}>{p.pendentes} pendente{p.pendentes!==1?"s":""}</span>}
                    </div>
                  </div>
                  <div style={{ paddingLeft:10, borderLeft:`3px solid ${T.hair}` }}>
                    {p.condos.map(c=>(
                      <div key={c.name} style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:3 }}>
                        <span style={{ color:"#444", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"80%" }}>{c.name}</span>
                        <span style={{ color:"#888", flexShrink:0, marginLeft:8 }}>{c.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={()=>setMonthModal(null)}>Fechar</Button></Modal.Footer>
      </Modal>

      {/* ── Modal top condominios ─────────────────────────────────────────── */}
      <Modal show={showCondoModal} onHide={()=>setShowCondoModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", paddingRight:8, gap:12 }}>
            <Modal.Title style={{ fontSize:16, fontWeight:600 }}>Top Condomínios — {condosView==="30d"?"Mês atual":`Últimos ${condosView} meses`}</Modal.Title>
            <div style={{ display:"flex", gap:8 }}>
              <Seg value={condosView} onChange={setCondosView} options={[{v:"30d",l:"mês"},{v:3,l:"3m"},{v:6,l:"6m"},{v:12,l:"12m"}]} />
              <Seg value={condoModalSort} onChange={setCondoModalSort} options={[{v:"count",l:"Ativações"},{v:"pct",l:"% Ating."}]} />
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {topCondosAll.length===0 ? <div style={{ color:T.ink3 }}>Sem dados no período.</div> : (() => {
            const sorted = [...topCondosAll].sort((a,b)=>condoModalSort==="pct"?(b.totalUnits?b.count/b.totalUnits:-1)-(a.totalUnits?a.count/a.totalUnits:-1):b.count-a.count);
            const maxVal = sorted.reduce((m,x)=>Math.max(m,x.count),1);
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {sorted.map((item,i)=>{
                  const pct   = item.totalUnits?Math.round((item.count/item.totalUnits)*100):null;
                  const barW  = item.totalUnits?`${Math.min((item.count/item.totalUnits)*100,100)}%`:`${(item.count/maxVal)*100}%`;
                  const barC  = pct===null?T.accent:pct>=80?T.ok:pct>=40?T.warn:T.accent;
                  return (
                    <div key={item.name}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:13.5, marginBottom:4 }}>
                        <span style={{ color:T.ink, display:"flex", alignItems:"center", gap:6 }}><span style={{ fontSize:11, color:T.ink4 }}>#{i+1}</span>{item.name}</span>
                        <span style={{ fontSize:12.5, flexShrink:0, textAlign:"right" }}>
                          {item.totalUnits ? (
                            <><span style={{ fontWeight:600, color:T.ink2, fontVariantNumeric:"tabular-nums" }}>{item.count}/{item.totalUnits} ativ.</span><span style={{ marginLeft:6, fontWeight:600, fontSize:11, color:barC, background:pct>=80?"#d1fae5":pct>=40?"#fff3cd":"#e8f0fe", borderRadius:4, padding:"1px 5px" }}>{pct}%</span></>
                          ) : <span style={{ color:T.ink3, fontVariantNumeric:"tabular-nums" }}>{item.count} ativações</span>}
                        </span>
                      </div>
                      <div style={{ height:5, background:"#F2F2F0", borderRadius:4 }}><div style={{ height:5, width:barW, background:barC, borderRadius:4, transition:"width .3s" }} /></div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={()=>setShowCondoModal(false)}>Fechar</Button></Modal.Footer>
      </Modal>
    </div>
  );
}
